import React, { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import MasterLayout from "../masterLayout/MasterLayout";
import { AppContext } from "../context/AppContext";
import Utils from "../utils/Utils";
import { useNavigate } from "react-router-dom";
import * as Fi from "react-icons/fi";
import * as Fa from "react-icons/fa";
import { toast } from "react-toastify";
import { MdKeyboardArrowLeft } from "react-icons/md";
import Skeleton from "react-loading-skeleton";
import TablePagination, {
  DEFAULT_PAGE_SIZE,
  paginateList,
} from "../components/custom/TablePagination";
import "../styles/style.css";

function ReservationClosures() {
  const navigate = useNavigate();
  const {
    removeHolidaySingle,
    holidaysLoading,
    closureList,
    reservationLoading,
    createNewClosure,
    closuresAllHistory,
    closuresHistory,
    getSettings,
  } = useContext(AppContext);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [exception, setException] = useState({
    startDate: "",
    endDate: "",
    reason: "",
    status: "Active",
  });
  const [selectedRemoveIndex, setSelectedRemoveIndex] = useState(-1);
  const [closuresTab, setClosuresTab] = useState("active");
  const [closuresPage, setClosuresPage] = useState(1);

  useEffect(() => {
    setClosuresPage(1);
  }, [closuresTab, closureList, closuresHistory]);

  useEffect(() => {
    if (!sheetOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [sheetOpen]);

  const closuresSource =
    closuresTab === "active" ? closureList : closuresHistory;
  const closuresPaged = useMemo(
    () => paginateList(closuresSource, closuresPage, DEFAULT_PAGE_SIZE),
    [closuresSource, closuresPage]
  );

  const handleExceptions = (e) => {
    const { name, value } = e.target;
    setException((prev) => {
      if (name === "startDate" && value) {
        const startDate = new Date(value);
        const endDate = new Date(startDate);
        endDate.setHours(startDate.getHours() + 12);
        return {
          ...prev,
          startDate: value,
          endDate: endDate.toISOString().slice(0, 16),
        };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleDeleteHoliday = async (id, index) => {
    if (!id) return;

    const token = localStorage.getItem("UserRetention");
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    try {
      setSelectedRemoveIndex(index);
      await removeHolidaySingle(id, {
        onSuccess: async () => {
          toast.success("Holiday is removed");
          await closuresAllHistory();
          await getSettings();
        },
        onFailed: (err) => {
          toast.error("Something went wrong!");
          console.log("Error on deleting holiday", err);
        },
        headers,
      });
    } finally {
      setSelectedRemoveIndex(-1);
    }
  };

  const exceptionEmptyValidation = () => {
    if (
      !exception ||
      exception?.endDate.length == 0 ||
      exception.startDate.length === 0 ||
      exception.status.length === 0
    ) {
      toast.error("Please fill required fields!");
      return false;
    }
    return true;
  };

  const closeSheet = () => {
    setException({ startDate: "", endDate: "", reason: "", status: "Active" });
    setSheetOpen(false);
  };

  const handleAddNewHoliday = async (e) => {
    e.preventDefault();
    if (exceptionEmptyValidation() === false) return;

    const payload = {
      startTime: Utils.formatExceptionDatetime(exception.startDate),
      endTime: Utils.formatExceptionDatetime(exception.endDate),
      reason: exception.reason,
      status: exception.status,
      source: "REACT",
    };

    await createNewClosure(payload, {
      onSuccess: async () => {
        toast.success("Created successfully!");
        await getSettings();
        await closuresAllHistory();
        closeSheet();
      },
      onFailed: (err) => {
        toast.error("Holiday is not created!");
        console.log("holiday creation error", err);
      },
    });
  };

  const today = new Date().toISOString().slice(0, 16);

  const sheet =
    sheetOpen && typeof document !== "undefined"
      ? createPortal(
          <div className="dt-sheet" role="presentation">
            <button
              type="button"
              className="dt-sheet__backdrop"
              aria-label="Close sheet"
              onClick={closeSheet}
            />
            <aside
              className="dt-sheet__panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="closure-sheet-title"
            >
              <div className="dt-sheet__handle" aria-hidden />
              <header className="dt-sheet__header">
                <h2 id="closure-sheet-title" className="dt-sheet__title">
                  Add New Closure
                </h2>
                <button
                  type="button"
                  className="dt-sheet__close"
                  onClick={closeSheet}
                  aria-label="Close"
                >
                  <Fi.FiX />
                </button>
              </header>

              <form
                className="dt-form dt-sheet__body"
                onSubmit={handleAddNewHoliday}
              >
                <p className="dt-sheet__hint">
                  Define special opening hours for holidays, events or other
                  needs. Leave the time empty if you&apos;re closed all day.
                </p>

                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="closure-start">
                    Start Date
                  </label>
                  <input
                    id="closure-start"
                    type="datetime-local"
                    name="startDate"
                    className="form-control"
                    onChange={handleExceptions}
                    value={exception.startDate}
                    min={today}
                  />
                </div>

                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="closure-end">
                    End Date
                  </label>
                  <input
                    id="closure-end"
                    type="datetime-local"
                    name="endDate"
                    className="form-control"
                    onChange={handleExceptions}
                    value={exception.endDate}
                    min={today}
                  />
                </div>

                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="closure-status">
                    Status
                  </label>
                  <select
                    id="closure-status"
                    className="form-select"
                    value={exception.status}
                    onChange={(e) => {
                      setException((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }));
                    }}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Hold">Hold</option>
                  </select>
                </div>

                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="closure-reason">
                    Comments
                  </label>
                  <textarea
                    id="closure-reason"
                    name="reason"
                    rows={4}
                    className="form-control"
                    onChange={handleExceptions}
                    value={exception.reason}
                  />
                </div>

                <div className="dt-form__actions">
                  <button
                    type="button"
                    className="el-card__btn el-card__btn--ghost"
                    onClick={closeSheet}
                    disabled={reservationLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="el-card__btn el-card__btn--primary"
                    disabled={reservationLoading}
                  >
                    {reservationLoading ? "Please wait…" : "Add closure"}
                  </button>
                </div>
              </form>
            </aside>
          </div>,
          document.body
        )
      : null;

  return (
    <Fragment>
      <MasterLayout>
        {sheet}

        <div className="rs-settings">
          <header className="rs-settings__header">
            <button
              type="button"
              className="rs-settings__back"
              onClick={() => navigate("/reservation-settings")}
            >
              <MdKeyboardArrowLeft /> More
            </button>
            <div className="rs-settings__heading">
              <div className="rs-settings__heading-text">
                <h1 className="rs-settings__title">Closures</h1>
                <p className="rs-settings__subtitle">
                  Pause bookings for holidays, events, or maintenance.
                </p>
              </div>
              <button
                type="button"
                className="rs-settings__edit"
                onClick={() => setSheetOpen(true)}
                disabled={reservationLoading}
              >
                <Fi.FiPlus /> Add closure
              </button>
            </div>
          </header>

          <section className="rs-settings-card">
            <div className="rs-settings-closures__bar">
              <div
                className="rs-settings-tabs"
                role="tablist"
                aria-label="Closures"
              >
                <button
                  type="button"
                  role="tab"
                  className={
                    closuresTab === "active"
                      ? "rs-settings-tabs__tab is-active"
                      : "rs-settings-tabs__tab"
                  }
                  aria-selected={closuresTab === "active"}
                  onClick={() => setClosuresTab("active")}
                >
                  Active ({closureList?.length ?? 0})
                </button>
                <button
                  type="button"
                  role="tab"
                  className={
                    closuresTab === "history"
                      ? "rs-settings-tabs__tab is-active"
                      : "rs-settings-tabs__tab"
                  }
                  aria-selected={closuresTab === "history"}
                  onClick={() => setClosuresTab("history")}
                >
                  History ({closuresHistory?.length ?? 0})
                </button>
              </div>
            </div>

            {reservationLoading ? (
              <div className="rs-settings-table-wrap">
                <table className="rs-settings-table">
                  <tbody>
                    {[...Array(3)].map((_, i) => (
                      <tr key={i}>
                        <td>
                          <Skeleton />
                        </td>
                        <td>
                          <Skeleton />
                        </td>
                        <td>
                          <Skeleton />
                        </td>
                        <td>
                          <Skeleton circle width={24} height={24} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : closuresPaged.total === 0 ? (
              <div className="rs-settings-empty-card">
                <Fi.FiCalendar className="rs-settings-empty-card__icon" />
                <p className="rs-settings-empty-card__title">
                  {closuresTab === "active"
                    ? "No active closures"
                    : "No closure history"}
                </p>
                <p className="rs-settings-empty-card__text">
                  {closuresTab === "active"
                    ? "Create a closure when your restaurant needs to stop taking bookings."
                    : "Past closures will show up here after they end."}
                </p>
                {closuresTab === "active" && (
                  <button
                    type="button"
                    className="rs-settings__edit"
                    onClick={() => setSheetOpen(true)}
                  >
                    <Fi.FiPlus /> Add closure
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="rs-settings-table-wrap dt-table-desktop">
                  <table className="rs-settings-table">
                    <thead>
                      <tr>
                        <th>Reason</th>
                        <th>Start</th>
                        <th>End</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {closuresPaged.items.map((exc, index) => {
                        const absoluteIndex =
                          (closuresPaged.page - 1) * DEFAULT_PAGE_SIZE + index;
                        const rowKey =
                          closuresTab === "active" ? absoluteIndex : exc?.id;
                        return (
                          <tr key={rowKey}>
                            <td className="dt-name">{exc?.reason || "N/A"}</td>
                            <td>
                              {exc?.startTime
                                ? Utils.parseAndFormat2(exc.startTime)
                                : "N/A"}
                            </td>
                            <td>
                              {exc?.endTime
                                ? Utils.parseAndFormat2(exc.endTime)
                                : "N/A"}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="rs-settings-trash"
                                onClick={() =>
                                  handleDeleteHoliday(exc.id, rowKey)
                                }
                                disabled={holidaysLoading}
                                aria-label="Delete closure"
                              >
                                {selectedRemoveIndex === rowKey &&
                                holidaysLoading ? (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <Fa.FaRegTrashAlt />
                                )}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="dt-card-list" aria-label="Closures">
                  {closuresPaged.items.map((exc, index) => {
                    const absoluteIndex =
                      (closuresPaged.page - 1) * DEFAULT_PAGE_SIZE + index;
                    const rowKey =
                      closuresTab === "active" ? absoluteIndex : exc?.id;
                    return (
                      <article key={rowKey} className="dt-card">
                        <div className="dt-card__top">
                          <div>
                            <h3 className="dt-card__name">
                              {exc?.reason || "Closure"}
                            </h3>
                            <p className="dt-card__meta">
                              {exc?.startTime
                                ? Utils.parseAndFormat2(exc.startTime)
                                : "N/A"}{" "}
                              →{" "}
                              {exc?.endTime
                                ? Utils.parseAndFormat2(exc.endTime)
                                : "N/A"}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="rs-settings-trash"
                            onClick={() =>
                              handleDeleteHoliday(exc.id, rowKey)
                            }
                            disabled={holidaysLoading}
                            aria-label="Delete closure"
                          >
                            {selectedRemoveIndex === rowKey &&
                            holidaysLoading ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              />
                            ) : (
                              <Fa.FaRegTrashAlt />
                            )}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>

                <TablePagination
                  page={closuresPaged.page}
                  pageSize={DEFAULT_PAGE_SIZE}
                  total={closuresPaged.total}
                  onPageChange={setClosuresPage}
                />
              </>
            )}
          </section>
        </div>
      </MasterLayout>
    </Fragment>
  );
}

export default ReservationClosures;
